import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UsersService } from '../../../service/entity-services/users.service';
import { normalizeEmail } from '../../../shared/utils/normalize-email';

export interface EditUserNameData {
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Admin dialog to edit an existing user's first/last name from Manage Users.
 * Reuses the same name-write the Profile dialog / first-login prompt use —
 * `UsersService.updateUserProfile` → PUT /users/profile — which the backend
 * confirmed is admin-write-any, non-destructive (preserves role/activated/deleted),
 * and root-writing. Email is the record key, so it's read-only. Returns `true` on save.
 */
@Component({
  selector: 'app-edit-user-name-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>edit</mat-icon>
      Edit Name
    </h2>

    <mat-dialog-content>
      <form [formGroup]="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Email</mat-label>
          <input matInput [value]="email" disabled>
          <mat-icon matSuffix>email</mat-icon>
          <mat-hint>Email is the account key and can't be changed here.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>First Name</mat-label>
          <input matInput formControlName="firstName" placeholder="Enter first name">
          <mat-icon matSuffix>person</mat-icon>
          @if (form.get('firstName')?.hasError('required')) {
            <mat-error>First name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Last Name</mat-label>
          <input matInput formControlName="lastName" placeholder="Enter last name">
          <mat-icon matSuffix>person</mat-icon>
          @if (form.get('lastName')?.hasError('required')) {
            <mat-error>Last name is required</mat-error>
          }
        </mat-form-field>

        @if (errorMessage()) {
          <p class="error-banner">{{ errorMessage() }}</p>
        }
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="onCancel()" [disabled]="saving()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button mat-raised-button color="primary"
              (click)="onSave()"
              [disabled]="form.invalid || saving()">
        @if (saving()) {
          <mat-spinner diameter="18"></mat-spinner>
        } @else {
          <mat-icon>save</mat-icon>
        }
        {{ saving() ? 'Saving...' : 'Save' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
    }
    mat-dialog-content {
      padding: 24px;
      min-width: 360px;
    }
    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }
    .error-banner {
      margin: 8px 0 0;
      color: #b00020;
      font-size: 13px;
    }
    mat-dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
    }
    mat-dialog-actions button { margin-left: 8px; }
    mat-dialog-actions button mat-icon,
    mat-dialog-actions button mat-spinner { margin-right: 4px; }
  `]
})
export class EditUserNameDialogComponent {
  private dialogRef = inject(MatDialogRef<EditUserNameDialogComponent>);
  private fb = inject(FormBuilder);
  private usersService = inject(UsersService);
  private data = inject<EditUserNameData>(MAT_DIALOG_DATA);

  readonly email: string;
  form: FormGroup;
  saving = signal(false);
  errorMessage = signal('');

  constructor() {
    this.email = normalizeEmail(this.data.email);
    this.form = this.fb.group({
      firstName: [this.data.firstName || '', Validators.required],
      lastName: [this.data.lastName || '', Validators.required],
    });
  }

  async onSave() {
    if (this.form.invalid || this.saving()) return;

    this.saving.set(true);
    this.errorMessage.set('');
    const { firstName, lastName } = this.form.value;

    try {
      await this.usersService.updateUserProfile({ email: this.email, firstName, lastName });
      this.dialogRef.close(true);
    } catch (err: any) {
      this.saving.set(false);
      if (err?.status === 403) {
        this.errorMessage.set('You do not have permission to edit this user.');
      } else if (err?.status === 401) {
        this.errorMessage.set('Your session expired. Please sign in again.');
      } else {
        this.errorMessage.set('Could not save the name. Please try again.');
      }
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
